import { z } from 'zod';
import { Decimal } from 'decimal.js';
import { amountScale, currencyCodeSchema } from './currency.js';
import { calendarMonthSchema, monthsBetween } from './installments.js';
import { displayScale, fitsCurrencyScale, isNegativeMoney, moneyAmountSchema } from './money.js';
import { isoDateSchema, positiveAmountSchema } from './transactions.js';
/**
 * Budgets, children's monthly limits and savings goals (Phase 5 PR 2, docs/plans/phase-5.md).
 * Budgets and limits are in the family's base currency; other currencies count with each
 * posting's own base_amount, so a past month never changes. Spent is always summed by
 * PostgreSQL over what the viewer can see.
 */
const isMoneyString = (v) => typeof v === 'string' && moneyAmountSchema.safeParse(v).success;
/** Zero or more; zero removes a budget or a limit from this month on. */
export const budgetAmountSchema = moneyAmountSchema.refine((v) => isMoneyString(v) && !isNegativeMoney(v), 'Amount cannot be negative');
/** From this share of a budget or limit (percent) the bar turns to "warning". */
export const BUDGET_WARNING_PERCENT = 80;
export const BUDGET_STATUSES = ['normal', 'warning', 'over'];
export const budgetStatusSchema = z.enum(BUDGET_STATUSES);
const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });
/** Normal below 80%, warning from 80%, over at 100% and beyond (exact decimals). */
export function budgetStatus(spent, amount) {
    const s = new D(moneyAmountSchema.parse(spent));
    const a = new D(moneyAmountSchema.parse(amount));
    if (s.gte(a))
        return 'over';
    if (s.times(100).gte(a.times(BUDGET_WARNING_PERCENT)))
        return 'warning';
    return 'normal';
}
// ---------------------------------------------------------------------------------------------
// Budgets
// ---------------------------------------------------------------------------------------------
/** PUT /api/v1/budgets: sets one budget from the current month on (amount 0 removes it). */
export const setBudgetRequestSchema = z.object({
    /** null: the overall household budget. */
    categoryId: z.string().uuid().nullable(),
    amount: budgetAmountSchema,
});
export const budgetQuerySchema = z.object({ month: calendarMonthSchema.optional() });
export const budgetLineSchema = z.object({
    /** null on the overall budget. */
    categoryId: z.string().uuid().nullable(),
    name: z.string().nullable(),
    /** null: no budget (a category shown only for what was spent). */
    amount: moneyAmountSchema.nullable(),
    spent: moneyAmountSchema,
    /** Negative once over budget. */
    remaining: moneyAmountSchema.nullable(),
    /** Whole percent for the bar, capped at 100. */
    percent: z.string().nullable(),
    status: budgetStatusSchema.nullable(),
});
export const budgetMonthSchema = z.object({
    month: calendarMonthSchema,
    currentMonth: calendarMonthSchema,
    /** The family's base currency: every amount here is in it. */
    currency: currencyCodeSchema,
    overall: budgetLineSchema,
    /** Categories with a budget, then categories spent on without one. */
    categories: z.array(budgetLineSchema),
    canManage: z.boolean(),
});
// ---------------------------------------------------------------------------------------------
// Children's monthly limits
// ---------------------------------------------------------------------------------------------
/** PUT /api/v1/limits/:memberId (amount 0 removes the limit from this month on). */
export const setLimitRequestSchema = z.object({ amount: budgetAmountSchema });
export const memberLimitSchema = z.object({
    memberId: z.string().uuid(),
    displayName: z.string(),
    /** null: no limit this month. */
    amount: moneyAmountSchema.nullable(),
    spent: moneyAmountSchema,
    /** "Left this month"; negative once over. */
    left: moneyAmountSchema.nullable(),
    percent: z.string().nullable(),
    status: budgetStatusSchema.nullable(),
    over: z.boolean(),
});
export const limitListSchema = z.object({
    month: calendarMonthSchema,
    currency: currencyCodeSchema,
    /** Parents: every child. A child: themselves. Anyone else: nobody. */
    items: z.array(memberLimitSchema),
    canManage: z.boolean(),
});
// ---------------------------------------------------------------------------------------------
// Savings goals
// ---------------------------------------------------------------------------------------------
/** POST /api/v1/goals and PATCH /api/v1/goals/:id. */
export const goalRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(60),
    targetAmount: positiveAmountSchema,
    currency: currencyCodeSchema,
    targetDate: isoDateSchema.nullable().default(null),
    /** The linked money account (the whole account, every currency). */
    accountId: z.string().uuid(),
    /** Family-wide (no owner) or personal (the person who creates it). */
    familyWide: z.boolean().default(false),
})
    .superRefine((v, ctx) => {
    if (isMoneyString(v.targetAmount) &&
        !fitsCurrencyScale(v.targetAmount, amountScale(v.currency))) {
        ctx.addIssue({
            code: 'custom',
            path: ['targetAmount'],
            message: `${v.currency} amounts allow at most ${amountScale(v.currency)} decimals`,
        });
    }
});
export const goalSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    targetAmount: moneyAmountSchema,
    currency: currencyCodeSchema,
    targetDate: isoDateSchema.nullable(),
    accountId: z.string().uuid(),
    accountName: z.string(),
    /** null: family-wide. */
    ownerMemberId: z.string().uuid().nullable(),
    ownerName: z.string().nullable(),
    /** The linked account's balance in the goal's currency. */
    saved: moneyAmountSchema,
    /** What is still missing (never negative). */
    remaining: moneyAmountSchema,
    percent: z.string(),
    reached: z.boolean(),
    /** To put aside each month to reach the target on time; null without a target date. */
    monthlyNeeded: moneyAmountSchema.nullable(),
    /** The target date has passed and the goal is not reached. */
    late: z.boolean(),
    archived: z.boolean(),
    canManage: z.boolean(),
});
export const goalListSchema = z.object({ items: z.array(goalSchema) });
/**
 * What to put aside each month to reach a goal by its target date: the remaining amount spread
 * over the months from this one to the target's month (both counted), rounded up to the
 * currency's everyday decimals so the target is reached. "0" when nothing is missing; null when
 * the target month has passed.
 */
export function monthlyAmountNeeded(remaining, currency, today, targetDate) {
    const months = monthsBetween(today.slice(0, 7), targetDate.slice(0, 7)) + 1;
    const left = new D(moneyAmountSchema.parse(remaining));
    if (left.lte(0))
        return '0.0000';
    if (months < 1)
        return null;
    return left
        .dividedBy(months)
        .toDecimalPlaces(displayScale(currency), Decimal.ROUND_UP)
        .toFixed(4);
}
//# sourceMappingURL=budgets.js.map